/*
 * Longeva Aesthetics — PDP premium micro-interactions
 *
 * Companion to assets/longeva-pdp-enhance.js. Wires four vanilla-JS
 * micro-interactions onto the buy box:
 *   1. Sliding brass indicator under the selected variant pill.
 *   2. Vertical number-tick on the quantity selector.
 *   3. Add-to-basket loading + success states.
 *   4. Dispatch-cutoff scroll-in reveal + 60s live countdown.
 *
 * Vanilla. No libraries. GPU-friendly (transform + opacity only).
 * Reduced-motion respected.
 */

(() => {
  "use strict";

  const REDUCED_MOTION = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const ready = (fn) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", fn, { once: true });
    } else {
      fn();
    }
  };
  const $ = (sel, r = document) => r.querySelector(sel);
  const $$ = (sel, r = document) => Array.from(r.querySelectorAll(sel));

  // ───────────────────────── 1. Sliding variant indicator ─────────────────────────
  function wireVariantIndicator() {
    const fieldsets = $$(".variant-option--buttons");
    fieldsets.forEach((fs) => {
      if (fs.dataset.longevaIndicator === "1") return;
      fs.dataset.longevaIndicator = "1";

      const indicator = document.createElement("span");
      indicator.className = "longeva-variant-indicator";
      indicator.setAttribute("aria-hidden", "true");
      fs.appendChild(indicator);

      const placeIndicator = (animate = true) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (!checked) return;
        const label = checked.nextElementSibling || fs.querySelector(`label[for="${checked.id}"]`);
        if (!label) return;
        const fsRect = fs.getBoundingClientRect();
        const lRect = label.getBoundingClientRect();
        const x = lRect.left - fsRect.left;
        const w = lRect.width;
        if (!animate || REDUCED_MOTION) {
          indicator.style.transition = "none";
          indicator.style.transform = `translateX(${x}px)`;
          indicator.style.width = `${w}px`;
          // Reflow + restore.
          void indicator.offsetWidth;
          indicator.style.transition = "";
        } else {
          indicator.style.transform = `translateX(${x}px)`;
          indicator.style.width = `${w}px`;
        }
      };

      // Initial placement.
      requestAnimationFrame(() => placeIndicator(false));

      // Re-place on any radio change inside this fieldset.
      fs.addEventListener("change", () => placeIndicator(true));
      fs.addEventListener("click", () => requestAnimationFrame(() => placeIndicator(true)));

      // Re-place on resize (pill widths can change at breakpoints).
      let resizeTimer;
      window.addEventListener("resize", () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => placeIndicator(false), 100);
      }, { passive: true });
    });
  }

  // ───────────────────────── 2. Quantity tick ─────────────────────────
  function wireQuantityTick() {
    const wrappers = $$(".quantity-selector, quantity-selector .quantity-selector").filter(
      (el) => !el.dataset.longevaTick
    );
    wrappers.forEach((q) => {
      q.dataset.longevaTick = "1";
      const input = q.querySelector('input[name="quantity"], [data-quantity-input]');
      if (!input) return;

      // Build a visual sibling tick. Native input stays for form submission
      // and accessibility (it carries the value attribute).
      const tick = document.createElement("span");
      tick.className = "longeva-qty-tick";
      tick.setAttribute("aria-hidden", "true");
      const initial = document.createElement("span");
      initial.className = "longeva-qty-tick__cell";
      initial.textContent = input.value || "1";
      tick.appendChild(initial);
      input.style.opacity = "0";
      input.style.position = "absolute";
      input.style.pointerEvents = "none";
      input.style.width = "24px";
      input.parentNode.insertBefore(tick, input);

      const updateTick = (newVal, direction) => {
        if (REDUCED_MOTION) {
          tick.innerHTML = "";
          const cell = document.createElement("span");
          cell.className = "longeva-qty-tick__cell";
          cell.textContent = newVal;
          tick.appendChild(cell);
          return;
        }
        const existing = tick.querySelector(".longeva-qty-tick__cell:not([data-leaving])");
        if (existing) {
          existing.setAttribute("data-leaving", "1");
          existing.dataset.state = direction === "up" ? "exit-up" : "exit-down";
          setTimeout(() => existing.remove(), 200);
        }
        const next = document.createElement("span");
        next.className = "longeva-qty-tick__cell";
        next.dataset.state = direction === "up" ? "enter-up" : "enter-down";
        next.textContent = newVal;
        tick.appendChild(next);
      };

      let lastVal = parseInt(input.value || "1", 10);
      const onChange = () => {
        const nv = parseInt(input.value || "1", 10);
        if (nv === lastVal) return;
        updateTick(String(nv), nv > lastVal ? "up" : "down");
        lastVal = nv;
      };
      input.addEventListener("input", onChange);
      input.addEventListener("change", onChange);

      // Watch programmatic Horizon updates (it sets input.value via JS, not events).
      const obs = new MutationObserver(() => {
        if (input.value !== String(lastVal)) onChange();
      });
      obs.observe(input, { attributes: true, attributeFilter: ["value"] });

      // Poll fallback (Horizon flips value imperatively without firing events).
      setInterval(() => {
        if (input.value !== String(lastVal)) onChange();
      }, 200);
    });
  }

  // ───────────────────────── 3. Add-to-basket states ─────────────────────────
  function wireAddToBasketStates() {
    const btn = $(".add-to-cart-button, [data-testid='add-to-cart-button']");
    if (!btn || btn.dataset.longevaState === "wired") return;
    btn.dataset.longevaState = "wired";

    const setState = (state) => {
      if (state) btn.setAttribute("data-longeva-state", state);
      else btn.removeAttribute("data-longeva-state");
      btn.setAttribute("aria-busy", state === "loading" ? "true" : "false");
    };

    // Hook Shopify's cart fetch. Horizon dispatches a `cart:update` event on
    // success; on click we flip to loading immediately.
    btn.addEventListener("click", () => setState("loading"));

    const announce = (msg) => {
      let live = $("#longeva-pdp-live");
      if (!live) {
        live = document.createElement("div");
        live.id = "longeva-pdp-live";
        live.setAttribute("aria-live", "polite");
        live.setAttribute("role", "status");
        live.style.cssText = "position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden;";
        document.body.appendChild(live);
      }
      live.textContent = msg;
    };

    const onCartChange = () => {
      setState("success");
      announce("Item added to basket.");
      setTimeout(() => setState(null), 1800);
    };

    document.addEventListener("cart:update", onCartChange);
    document.addEventListener("cart:added", onCartChange);
    // Some Horizon variants fire on the `<cart-drawer>` element.
    const cartDrawer = document.querySelector("cart-drawer, [class*='cart-drawer']");
    if (cartDrawer) cartDrawer.addEventListener("cart:refresh", onCartChange);

    // Failsafe: if no event fires within 4s, drop loading state.
    let watchdog = null;
    btn.addEventListener("click", () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => {
        if (btn.getAttribute("data-longeva-state") === "loading") setState(null);
      }, 4000);
    });
  }

  // ───────────────────────── 4. Dispatch reveal + countdown ─────────────────────────
  function wireDispatch() {
    const line = $(".longeva-pdp-dispatch");
    if (!line || line.dataset.longevaRevealed === "1") return;
    line.dataset.longevaRevealed = "1";
    line.setAttribute("data-revealed", "false");

    if ("IntersectionObserver" in window) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              line.setAttribute("data-revealed", "true");
              io.disconnect();
            }
          });
        },
        { threshold: 0.5 }
      );
      io.observe(line);
    } else {
      line.setAttribute("data-revealed", "true");
    }

    // Live countdown chip — only on weekdays before 15:00 UK time.
    const renderCountdown = () => {
      const now = new Date();
      // UK time approximation via Europe/London offset.
      // Sufficient for an hours-minutes display.
      const partsFmt = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/London",
        weekday: "short",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });
      const parts = Object.fromEntries(
        partsFmt.formatToParts(now).map((p) => [p.type, p.value])
      );
      const weekday = parts.weekday;
      const hour = parseInt(parts.hour, 10);
      const minute = parseInt(parts.minute, 10);
      // Cutoff is 15:00.
      const minutesNow = hour * 60 + minute;
      const cutoff = 15 * 60;
      const isWeekend = weekday === "Sat" || weekday === "Sun";
      let chip = line.querySelector(".longeva-pdp-dispatch__countdown");
      if (isWeekend || minutesNow >= cutoff) {
        if (chip) chip.remove();
        return;
      }
      const remaining = cutoff - minutesNow;
      const h = Math.floor(remaining / 60);
      const m = remaining % 60;
      const label = h > 0 ? `${h}h ${m}m left` : `${m}m left`;
      if (!chip) {
        chip = document.createElement("span");
        chip.className = "longeva-pdp-dispatch__countdown";
        line.appendChild(chip);
      }
      chip.textContent = label;
    };

    renderCountdown();
    // Update every 60 seconds (not every second — no need to poll more frequently).
    setInterval(renderCountdown, 60_000);
  }

  // ───────────────────────── boot ─────────────────────────
  ready(() => {
    const runAll = () => {
      try { wireVariantIndicator(); } catch (_) {}
      try { wireQuantityTick(); } catch (_) {}
      try { wireAddToBasketStates(); } catch (_) {}
      try { wireDispatch(); } catch (_) {}
    };
    runAll();
    setTimeout(runAll, 400);
    setTimeout(runAll, 1500);
  });
})();
